import type { SchemaToType } from '@main/schema-utils';
import { mkInput, mkType } from '@main/schema-utils';

import { FeatureDefinitionName } from './enums';
import { FeatureGate, SupportTier } from './enums/featureSet';
import { FeatureDefinition } from './featureDefinition';
import { UserPreview } from './userPreview';

export const Feature = mkType({
  name: 'Feature',
  comment:
    'A feature that can be enabled/disabled/configured for an organization',
  fields: {
    id: {
      comment: 'The id of feature.',
      type: 'id',
      modelName: 'feature',
    },
    lastEditor: {
      comment: 'The user who last opted in or out of the beta feature.',
      type: UserPreview,
      optional: true,
    },
    definition: {
      comment: 'The definition of the feature',
      type: FeatureDefinition,
    },
    featureName: {
      comment: 'The name of the feature definition',
      type: { FeatureDefinitionName },
    },
    isBetaEnabled: {
      comment: 'Whether the organization opted into the beta feature.',
      type: 'boolean',
      optional: true,
    },
    betaUpdatedAt: {
      comment: 'The date the organization opted into the beta feature.',
      type: 'Date',
      optional: true,
    },
    valueBoolean: {
      comment: 'The CX-chosen value for a boolean feature',
      type: 'boolean',
      optional: true,
    },
    valueStringList: {
      comment: 'The CX-chosen value for a string list feature',
      type: 'string',
      list: true,
      optional: true,
    },
    valueNumber: {
      comment: 'The CX-chosen value for a number feature',
      type: 'int',
      optional: true,
    },
    valueFeatureGate: {
      comment: 'The CX-chosen value for a support feature gate feature',
      type: { FeatureGate },
      optional: true,
    },
    valueSupportTier: {
      comment: 'The CX-chosen value for a support tier feature',
      type: { SupportTier },
      optional: true,
    },
  },
});

/** Override type */
export type Feature = SchemaToType<typeof Feature>;

export const UpdateFeatureInput = mkInput({
  name: 'UpdateFeatureInput',
  comment: 'Input for updating a feature',
  fields: {
    id: {
      comment: 'The id of the feature to update',
      type: 'id',
      modelName: 'feature',
    },
    isBetaEnabled: {
      comment:
        'Whether to opt the organization into the beta feature. Not applicable to non-beta features',
      type: 'boolean',
      optional: true,
    },
  },
});

/** Override type */
export type UpdateFeatureInput = SchemaToType<typeof UpdateFeatureInput>;

export const UpdateFeaturesInput = mkInput({
  name: 'UpdateFeaturesInput',
  comment: 'Input for bulk updating features',
  fields: {
    features: {
      comment:
        'List of features with the properties that should be updated for each',
      type: UpdateFeatureInput,
      list: true,
    },
  },
});

/** Override type */
export type UpdateFeaturesInput = SchemaToType<typeof UpdateFeaturesInput>;
